import React, { useMemo } from 'react';
import { unpackToDateTime } from '@campfire/hot-date';
import { Box, Typography } from '@material-ui/core';
import { AlertCard, AlertCardBody, getAlertCardColors } from '../../../common/cards/alert-card/AlertCard';
import { BulletinManagement } from './__generated__/BulletinManagement';
import { BulletinListRow } from './BulletinListRow';
import { bulletinMatchesSearch } from './bulletin-model';
import {
  AUDIENCE_WIDTH,
  AUTHOR_WIDTH,
  DATE_WIDTH,
  PIN_WIDTH,
  PIN_WIDTH_COMPACT,
  REPLIES_WIDTH,
  useBulletinRowStyles,
} from './bulletinStyles';

interface BulletinManagementListProps {
  bulletins: BulletinManagement[];
  search: string;
  activeBulletinId?: string;
  canUserAddEdit: boolean;
  panelOpen: boolean;
  isPinningId: string;
  onOpen: (bulletin: BulletinManagement) => void;
  onTogglePinned: (bulletin: BulletinManagement) => void;
}

const columnLabel = { fontSize: 12, fontWeight: 600, color: '#646464' } as const;

const BulletinManagementList = ({
  bulletins,
  search,
  activeBulletinId,
  canUserAddEdit,
  panelOpen,
  isPinningId,
  onOpen,
  onTogglePinned,
}: BulletinManagementListProps) => {
  const classes = useBulletinRowStyles({});
  const { lightTextColor } = getAlertCardColors('info');

  const ordered = useMemo(() => {
    const matching = bulletins.filter((bulletin) => bulletinMatchesSearch(bulletin, search));
    return [...matching].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return unpackToDateTime(b.dateCreated).valueOf() - unpackToDateTime(a.dateCreated).valueOf();
    });
  }, [bulletins, search]);

  return (
    <Box display='flex' flexDirection='column' flex={1} minWidth={0} minHeight={0}>
      {!canUserAddEdit ? (
        <Box flexShrink={0} padding='16px 28px 0 28px'>
          <AlertCard variant='info' title='Adding & Editing Not Available'>
            <AlertCardBody>
              <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}>
                Your organisation has turned off your ability to create and edit bulletins. Get in contact with one of
                your admins if you think this is a mistake.
              </Typography>
            </AlertCardBody>
          </AlertCard>
        </Box>
      ) : (
        <>
          <Box className={classes.columnHeader}>
            <Typography style={{ ...columnLabel, width: DATE_WIDTH, flexShrink: 0 }}>Posted</Typography>
            <Typography style={{ ...columnLabel, flexGrow: 1 }}>Bulletin</Typography>
            <Box width={panelOpen ? PIN_WIDTH_COMPACT : PIN_WIDTH} flexShrink={0} />
            <Typography style={{ ...columnLabel, width: AUDIENCE_WIDTH, flexShrink: 0 }}>Audience</Typography>
            {!panelOpen && (
              <Typography style={{ ...columnLabel, width: AUTHOR_WIDTH, flexShrink: 0 }}>Posted by</Typography>
            )}
            <Typography style={{ ...columnLabel, width: REPLIES_WIDTH, flexShrink: 0, textAlign: 'right' }}>
              Replies
            </Typography>
            <Box width={20} flexShrink={0} />
          </Box>

          <Box flexGrow={1} minHeight={0} style={{ overflowY: 'auto' }}>
            {ordered.length === 0 && (
              <Box display='flex' flexDirection='column' alignItems='center' padding='64px 24px'>
                <Typography style={{ fontSize: 16, fontWeight: 600, color: '#444444' }}>
                  {search ? 'No bulletins match that search' : 'No bulletins yet'}
                </Typography>
                <Typography
                  style={{ fontSize: 14, color: '#646464', marginTop: 8, textAlign: 'center', maxWidth: 420 }}
                >
                  {search
                    ? 'Try a different title, author or audience.'
                    : 'Bulletins are announcements your volunteers see on their dashboard.'}
                </Typography>
              </Box>
            )}

            {ordered.map((bulletin) => (
              <BulletinListRow
                key={bulletin.bulletinId}
                bulletin={bulletin}
                selected={bulletin.bulletinId === activeBulletinId}
                panelOpen={panelOpen}
                canUserAddEdit={canUserAddEdit}
                isPinning={isPinningId === bulletin.bulletinId}
                onOpen={onOpen}
                onTogglePinned={onTogglePinned}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
};

export { BulletinManagementList };
